import FormFieldWrapper from '@ai/common/src/sections/expert-configuration/FormFieldWrapper';
import { Dictor, ExpertDefaults } from '@ai/common/src/types/expert';
import {
  Divider,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VoiceGroup } from './SpeechTab';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { FormikProps } from 'formik';
import { Language } from '@ai/common/src/types/language';
import i18next from 'i18next';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { ExpertForm } from '@ai/common/src/types/expert';

export function VoiceAndCharacterSettings({
  expertConstants,
  form,
}: {
  expertConstants: ExpertDefaults;
  form: FormikProps<ExpertForm>;
}) {
  const { t } = useTranslation();
  const { dictors_map, emotions_map } = expertConstants;
  const { values, setFieldValue } = form;
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  const [currentAudio, setCurrentAudio] = useState<{ src: string; isPlaying: boolean }>({
    src: '',
    isPlaying: false,
  }); // этот стейт нужен для корректного отображения кнопок play/pause

  const [emotionsSelectMap, setEmotionsSelectMap] = useState<Record<string, string>>(
    (() => {
      const result: Record<string, string> = {};

      // проставляем дефолтные значения
      Object.entries(dictors_map).forEach(([key, value]) => {
        // emotion есть только у голосов Яндекса
        if (!key.startsWith('google') && !key.startsWith('elevenlabs')) {
          result[key] = value.emotions[0];
        }
      });

      // проставляем эмоцию выбранного диктора
      // дополнительная проверка на includes на случай, если массив emotions изменился
      if (dictors_map[values.config.voice].emotions.includes(values.config.emotion)) {
        result[values.config.voice] = values.config.emotion;
      }

      return result;
    })()
  );

  const onPlayPause = (audioSrc: string) => {
    if (audioRefs.current[audioSrc]?.paused) {
      const prevAudio = audioRefs.current[currentAudio.src];
      if (prevAudio) {
        prevAudio.pause();
        prevAudio.currentTime = 0;
      }

      audioRefs.current[audioSrc]?.play();
      audioRefs.current[audioSrc].onended = () =>
        setCurrentAudio({ src: audioSrc, isPlaying: false });

      setCurrentAudio({ src: audioSrc, isPlaying: true });
    } else {
      audioRefs.current[audioSrc]?.pause();
      setCurrentAudio({ src: audioSrc, isPlaying: false });
    }
  };

  const getAudioSrc = (dictorKey: string) => {
    const emotionKey = emotionsSelectMap[dictorKey] ? `_${emotionsSelectMap[dictorKey]}` : '';

    return `/voice_samples/${dictorKey}${emotionKey}.wav`;
  };

  const groupedDictors = useMemo(() => {
    const yandexVoices: Array<Dictor & { key: string }> = [];
    const googleVoices: Array<Dictor & { key: string }> = [];
    const elevenlabsVoices: Array<Dictor & { key: string }> = [];

    Object.entries(dictors_map).forEach(([key, value]) => {
      switch (true) {
        case key.startsWith('elevenlabs'):
          elevenlabsVoices.push({ ...value, key });
          break;

        case key.startsWith('google'):
          googleVoices.push({ ...value, key });
          break;

        default:
          yandexVoices.push({ ...value, key });
      }
    });

    return [
      { isSubheader: true, key: VoiceGroup.YANDEX },
      ...yandexVoices,
      { isSubheader: true, key: VoiceGroup.GOOGLE },
      ...googleVoices,
      { isSubheader: true, key: VoiceGroup.ELEVENLABS },
      ...elevenlabsVoices,
    ];
  }, [dictors_map]);

  const renderSelectedDictor = (dictorKey: string) => {
    const dictorName = dictors_map[dictorKey].translation[i18next.language as Language];
    const emotion =
      emotions_map[values.config.emotion] &&
      dictors_map[dictorKey].emotions.includes(values.config.emotion)
        ? emotions_map[values.config.emotion][i18next.language as Language]
        : '';

    const voiceGroup =
      (dictorKey.startsWith('elevenlabs') && t(`expertsList.voiceGroups.elevenlabsVoices`)) ||
      (dictorKey.startsWith('google') && t(`expertsList.voiceGroups.googleVoices`)) ||
      t(`expertsList.voiceGroups.yandexVoices`);

    return (
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <Typography fontWeight={500}>{dictorName}</Typography>

        {!!emotion && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: (theme) => theme.palette.neutral[200] }}
            />
            <Typography color="grey">{emotion}</Typography>
          </>
        )}

        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: (theme) => theme.palette.neutral[200] }}
        />
        <Typography color="grey">{voiceGroup}</Typography>
      </Stack>
    );
  };

  return (
    <FormFieldWrapper
      title={t('expertsList.voiceAndCharacter')}
      description={t('expertsList.voiceDescr')}
      fieldComponent={
        <Stack direction={'row'} gap={2}>
          <Select
            value={values.config.voice}
            onChange={(e) => {
              setFieldValue('config.voice', e.target.value);
              setFieldValue('config.emotion', emotionsSelectMap[e.target.value]);
            }}
            sx={{ background: 'white', width: '100%' }}
            MenuProps={{
              slotProps: { paper: { sx: { maxHeight: 500 } } },
            }}
            renderValue={renderSelectedDictor}
            onClose={() => {
              const current = audioRefs.current[currentAudio.src];
              if (current) {
                current.pause();
                current.currentTime = 0;
              }

              setCurrentAudio({ src: '', isPlaying: false });
            }}
          >
            {groupedDictors.map((dictor, ind) =>
              'isSubheader' in dictor ? (
                <ListSubheader
                  key={dictor.key}
                  sx={{
                    borderTop:
                      ind === 0 ? undefined : (theme) => `1px solid ${theme.palette.neutral[200]}`,
                  }}
                >
                  {t([
                    `expertsList.voiceGroups.${dictor.key}`,
                    'expertsList.voiceGroups.otherVoices',
                  ])}
                </ListSubheader>
              ) : (
                <MenuItem
                  key={dictor.key}
                  value={dictor.key}
                  sx={{ width: '100%', height: 60 }}
                  disableRipple
                >
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    flexGrow={1}
                  >
                    <Typography minWidth={100}>
                      {dictors_map[dictor.key].translation[i18next.language as Language]}
                    </Typography>

                    {!dictor.key.startsWith('google') && !dictor.key.startsWith('elevenlabs') && (
                      <Select
                        size="small"
                        value={emotionsSelectMap[dictor.key] || ''}
                        onChange={(e) => {
                          setEmotionsSelectMap((prev) => ({
                            ...prev,
                            [dictor.key]: e.target.value,
                          }));
                          if (dictor.key === values.config.voice) {
                            setFieldValue('config.emotion', e.target.value);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 200, background: 'white' }}
                        disabled={!(dictors_map[dictor.key]?.emotions || []).length}
                      >
                        {(dictors_map[dictor.key]?.emotions || []).map((emotion) => (
                          <MenuItem
                            key={emotion}
                            value={emotion}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {emotions_map[emotion][i18next.language as Language]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}

                    <audio
                      ref={(el) => (audioRefs.current[getAudioSrc(dictor.key)] = el)}
                      src={getAudioSrc(dictor.key)}
                    />

                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayPause(getAudioSrc(dictor.key));
                      }}
                    >
                      {currentAudio.src === getAudioSrc(dictor.key) && currentAudio.isPlaying ? (
                        <PauseCircleIcon fontSize="large" />
                      ) : (
                        <PlayCircleIcon fontSize="large" />
                      )}
                    </IconButton>
                  </Stack>
                </MenuItem>
              )
            )}
          </Select>
        </Stack>
      }
    />
  );
}
